import { FormField } from "@avamae/formbuilder";
import React, { useEffect, useRef } from "react";
import dropdownImage from "assets/Icon_Form_Arrow.png";
import { APP_ROUTES, PARENT_ID_KEY, endpoints } from "config";
import { components } from "react-select";
import { FormFieldTooltip } from "../../FormFieldTooltip/FormFieldTooltip";
import { EditMultiSelectReadOnly } from "./EditMultiSelectReadOnly";
import { MultiSelectSearchList } from "./MultiSelectSearchList";
import {
  MultiSelectSearchListWithLinks,
  SingleSelectSearchListWithLinks,
} from "./MultiSelectSearchListWithLinks";
import { EditSingleSelectReadOnly } from "./SingleSelectReadOnly";
import { SingleSelectSearchList } from "./SingleSelectSearchList";
import { SelectOption } from "../../../dropdown/Dropdown";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import { labelAfterContentFields } from "../CustomString/StringV2";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFormReadOnlyFields,
  setRedirectUrl,
  setRedirectUrlDestinationModule,
} from "reducers/listPage";
import { SingleSelectSearchListOrderNumbers } from "./SingleSelectSearchListOrderNumbers";
import { FormikContextType } from "formik";
import { SingleSelectOption } from "../SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { parseListDate } from "helpers/parseListDate";
import { RolesMultiSelectSearchListWithLinks } from "./RoleMultiSelectSearchListWithLinks";
import {
  setActionID,
  setActionKey,
  setTemporaryFilters,
} from "reducers/redirect";

import editIcon from "assets/edit_icon.png";

const tooltipMainIconSource = require("assets/Tooltip.png");
const tooltipCloseIconSource = require("assets/Icon_Btn_06.png");

export const customUseOnClickOutside = (
  callback: () => any,
  callOnEsc = true
) => {
  const ref = useRef<any>(null);
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // If we we haven't set the node yet, or a click occurs within the target, return.

      if (ref.current == null || ref.current.contains(e.target)) {
        return;
      }
      // If the click is a left click and outside the target,
      // run the callback.
      if (e.buttons === 1) {
        callback();
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (ref.current != null && callOnEsc) {
        if (e.keyCode === 27) {
          callback();
        }
      }
    };

    // Add event listener.
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("pointerdown", handleClick); // mobile
    document.addEventListener("keydown", handleEsc);

    // Tear down event listener on cleanup.
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("pointerdown", handleClick); // mobile
      document.removeEventListener("keydown", handleEsc);
    };
  }, [callback]);

  return { ref };
};

export const CustomValue: React.FC<any> = props => {
  const hideDelete = props.selectProps.selectProps.hideDelete;
  return (
    <components.SingleValue {...props}>
      <div className="ValueContainer">
        <div
          className={`CustomValueContainer ${hideDelete ? "HideDelete" : ""} `}
        >
          {props.children}
        </div>
        {!hideDelete && (
          <div
            className="Delete"
            onClick={props.selectProps.selectProps.remove}
            onPointerDown={props.selectProps.selectProps.remove} // NOTE: Needed for mobile
          >
            <svg
              height="14"
              width="14"
              viewBox="0 0 20 20"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
            </svg>
          </div>
        )}
      </div>
    </components.SingleValue>
  );
};

export const CustomControl: React.FC<any> = props => {
  const selectProps = props?.selectProps?.selectProps;

  return (
    <components.Control {...props}>
      <div
        className={"customControl"}
        // NOTE: Don't need as it causes dropdown to close automatically soon after opening it
        // onClick={() => {
        //   props.selectProps.selectProps.setMenuOpen(
        //     !props.selectProps.selectProps.menuOpen
        //   );
        // }}
        onClick={() => {
          if (selectProps) {
            if (
              selectProps.callSearchEndpointOnClick &&
              (!selectProps.searchString ||
                (selectProps.searchString &&
                  typeof selectProps.searchString === "string" &&
                  selectProps.searchString.trim() === "")) &&
              selectProps.parsedSearchEndpoint &&
              selectProps.fetchResults
            ) {
              selectProps.fetchResults("", selectProps.parsedSearchEndpoint);
            }
          }
        }}
      >
        {props.children}
      </div>
    </components.Control>
  );
};
export const CustomOption: React.FC<any> = props => {
  const { innerProps, innerRef } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`CustomOption ${props.selectProps.selectProps.fieldName} ${
        props.isSelected ? "Active" : ""
      }`}
      key={`custom-option-${props.selectProps.selectProps.fieldName}-${props.label}`}
    >
      <div className="Checkbox">
        <div className={`Radio`} role="radio" aria-checked={props.isSelected}>
          <input
            id={`custom-option-${props.selectProps.selectProps.fieldName}-${props.value}`}
            type="radio"
            name={`${props.selectProps.selectProps.fieldName}-${props.value}`}
            value=""
            disabled={false}
            onChange={() => {}} // Stop warning in console
            checked={props.isSelected}
          />
          <label
            className={`CustomInput`}
            htmlFor={`custom-option-${props.value}`}
          ></label>
        </div>
      </div>

      <div className="Label">{props.label ? props.label : ""}</div>
    </div>
  );
};

export const RolesCustomOptionWithLink: React.FC<any> = props => {
  const { innerProps, innerRef } = props;
  const fieldName = props.selectProps.selectProps.fieldName;
  const linkText = props.selectProps.selectProps.linkText;
  const onLinkClick = props.selectProps.selectProps.onLinkClick;
  const extraInfo = props.selectProps.selectProps.extraInfo;
  const bAssignedRole = extraInfo.find(
    (item: any) => item.value === props.value
  )?.bHasRole;
  let itemInSelectedOptions = false;

  if (
    props.value &&
    props.selectProps.selectProps.selectedOptions &&
    Array.isArray(props.selectProps.selectProps.selectedOptions) &&
    props.selectProps.selectProps.selectedOptions.filter(
      (item: any) => item && item.value === props.value
    ).length > 0
  ) {
    itemInSelectedOptions = true;
  }

  const linkOnClick: React.MouseEventHandler<
    HTMLDivElement
  > | null = onLinkClick
    ? e => {
        e.stopPropagation();
        onLinkClick(fieldName, props.data);
      }
    : null;

  return (
    <>
      <div
        ref={innerRef}
        {...innerProps}
        key={`custom-option-${fieldName}-${props.label}`}
      >
        <div
          className={`CustomOption ${fieldName} ${
            props.isSelected || itemInSelectedOptions ? "Active" : ""
          }`}
        >
          <div className="Checkbox">
            <div
              className={`Radio`}
              role="radio"
              aria-checked={props.isSelected}
            >
              <input
                id={`custom-option-${fieldName}-${props.value}`}
                type="radio"
                name={`${fieldName}-${props.value}`}
                value=""
                disabled={false}
                onChange={() => {}} // Stop warning in console
                checked={props.isSelected}
              />
              <label
                className={`CustomInput`}
                htmlFor={`custom-option-${props.value}`}
              ></label>
            </div>
          </div>
          <div className={`Label ${bAssignedRole ? "Assigned" : ""}`}>{`${
            props.label ? props.label : ""
          }`}</div>
          {linkText && onLinkClick && (
            <div className={`Link `}>
              {linkText && linkOnClick && (
                <div className="LinkRedirect" onClick={linkOnClick}>
                  {linkText ?? `View ${fieldName}`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const CustomOptionWithLink: React.FC<any> = props => {
  const { innerProps, innerRef } = props;
  const fieldName = props.selectProps.selectProps.fieldName;
  const linkText = props.selectProps.selectProps.linkText;
  const onLinkClick = props.selectProps.selectProps.onLinkClick;
  const onLinkNestedFormClick =
    props.selectProps.selectProps.onLinkNestedFormClick;
  const extraDataToDisplay = props.selectProps.selectProps.extraDataToDisplay;
  const expandableInfo = props.selectProps.selectProps.expandableInfo;
  const expandable = props.selectProps.selectProps.expandable;
  const bOnLinkClick = props.selectProps.selectProps.bOnLinkClick;
  const dispatch = useDispatch();
  const linkNestedFormOnClick: React.MouseEventHandler<
    HTMLDivElement
  > | null = onLinkNestedFormClick
    ? e => {
        e.stopPropagation();

        onLinkNestedFormClick(fieldName, props.data);
      }
    : null;

  const relevantExtraDataToDisplay: string | undefined =
    extraDataToDisplay && extraDataToDisplay.hasOwnProperty(props.value)
      ? extraDataToDisplay[props.value]
      : undefined;

  let itemInSelectedOptions = false;

  if (
    props.value &&
    props.selectProps.selectProps.selectedOptions &&
    Array.isArray(props.selectProps.selectProps.selectedOptions) &&
    props.selectProps.selectProps.selectedOptions.filter(
      (item: any) => item && item.value === props.value
    ).length > 0
  ) {
    itemInSelectedOptions = true;
  }

  const [expand, setExpand] = React.useState<boolean>(false);
  const targetExpandableInfo = expandableInfo?.find(
    (e: any) => e.value === props.value
  );

  const linkOnClick: React.MouseEventHandler<
    HTMLDivElement
  > | null = onLinkClick
    ? e => {
        e.stopPropagation();
        if (
          fieldName === "projectsID" &&
          window.location.pathname === APP_ROUTES.sites
        ) {
          dispatch(setTemporaryFilters(true));
          dispatch(
            setRedirectUrlDestinationModule(APP_ROUTES.projectmanagement)
          );
          dispatch(
            setRedirectUrl(
              `${endpoints.projectmanagement.list}?bTemporaryFilters=true&ProgrammesId=${targetExpandableInfo.programmesID}&bClearSearch=true&filters=Projects__ID+EQ+(${targetExpandableInfo.value})`
            )
          );
          // dispatch(setActionKey("VIEW"));
          // dispatch(setActionID(String(targetExpandableInfo.value)));
          window.localStorage.setItem(
            PARENT_ID_KEY,
            targetExpandableInfo.programmesID
          );
        }

        onLinkClick(fieldName, props.data);
      }
    : null;

  return (
    <>
      <div
        ref={innerRef}
        {...innerProps}
        key={`custom-option-${fieldName}-${props.label}`}
      >
        <div
          className={`CustomOption ${fieldName} ${
            props.isSelected || itemInSelectedOptions ? "Active" : ""
          } ${expand ? "Expanded" : "Collapsed"}`}
        >
          <div className="Checkbox">
            <div
              className={`Radio`}
              role="radio"
              aria-checked={props.isSelected}
            >
              <input
                id={`custom-option-${fieldName}-${props.value}`}
                type="radio"
                name={`${fieldName}-${props.value}`}
                value=""
                disabled={false}
                onChange={() => {}} // Stop warning in console
                checked={props.isSelected}
              />
              <label
                className={`CustomInput`}
                htmlFor={`custom-option-${props.value}`}
              ></label>
            </div>
          </div>

          <div className="Label">{`${
            props.label ? props.label : ""
          }${relevantExtraDataToDisplay ?? ""}`}</div>
          {((linkText && (linkNestedFormOnClick || onLinkClick)) ||
            (expandableInfo && expandable)) && (
            <div className={`Link ${expand ? "Expandable" : "Collapsable"}`}>
              {expandableInfo && expandable && (
                <div
                  className={expand ? "Expanded" : "Collapsed"}
                  onClick={e => {
                    e.stopPropagation();
                    setExpand(expand => !expand);
                  }}
                >
                  {expand ? "Show less" : "Show more"}
                </div>
              )}

              {linkText && linkNestedFormOnClick && !bOnLinkClick ? (
                <div className="LinkRedirect" onClick={linkNestedFormOnClick}>
                  {linkText ?? `View ${fieldName}`}
                </div>
              ) : linkText && linkOnClick ? (
                <div className="LinkRedirect" onClick={linkOnClick}>
                  {linkText ?? `View ${fieldName}`}
                </div>
              ) : null}
            </div>
          )}
        </div>
        {expand &&
          targetExpandableInfo &&
          Object.keys(targetExpandableInfo).length > 2 && (
            <div className="SelectOptionContent">
              {Object.entries(targetExpandableInfo ?? {})?.map(
                ([key, value], _index) => {
                  if (key === "addDate") {
                    value = parseListDate(String(value), false);
                  }
                  return key === "label" || key === "value" ? null : (
                    <div
                      className="OptionInfo"
                      style={{
                        marginTop: `${_index === 0 ? "0" : "5px"}`,
                      }}
                    >
                      <div className="OptionKey">{formatPropertyName(key)}</div>
                      <div className="OptionValue">{value ?? " - "}</div>
                    </div>
                  );
                }
              )}
            </div>
          )}
      </div>
    </>
  );
};

export type LinkDestination = { [fieldName: string]: APP_ROUTES };

export type ActionLinkConfig = {
  [key: string]: {
    text: string;
    permissionKey: string;
    location: string;
  };
};

export type SelectConfigOptionalSearchParameters = {
  [fieldName: string]: { [parameterName: string]: string }; // Where the string related to the parameter name is the fieldName's value you want to use
};
export type SelectConfig = {
  searchEndpoint?: string | { [key: string]: string };
  linkTextConfig?: ActionLinkConfig;
  onLinkClick?: (fieldName: string, opt: SelectOption) => void;
  handleActionLinkClicked?: (fieldName: string) => void;

  actionLinkConfig?: ActionLinkConfig;
  optionalSearchParameters?: SelectConfigOptionalSearchParameters;
};

export const searchListFieldRenderer = (
  type:
    | "singleselect"
    | "multiselect"
    | "multiselectWithLinks"
    | "singleSelectWithLinks"
    | "multiselectOrderNumbers"
    | "rolesMultiSelectWithLinks",
  config?: SelectConfig,
  afterLabel: labelAfterContentFields | null = null,
  readOnly?: boolean,
  editDisabled?: boolean,
  expandableFieldNames: string[] = [],
  nestedFormModule?: { [fieldName: string]: APP_ROUTES },
  overrideOptions?: {
    [fieldName: string]: (
      formik: FormikContextType<any>
    ) => [SingleSelectOption[], string?, string?];
  },
  textToDisplayAtTop?: string,
  showEditIcon?: boolean,
  maxHeight?: string,
  addZIndexToMenu?: boolean,
  callSearchEndpointOnClick?: boolean
) =>
  function searchListFieldRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    if (type === "singleselect") {
      return (
        <div
          key={`singleselect-${_key}`}
          className="FormBox FormSideBySide"
          style={maxHeight ? { maxHeight: `${maxHeight}` } : undefined}
        >
          <div className="FormLabel">
            <label>{field.label}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <SingleSelectSearchList
            name={String(field.name)}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            showUseDefaultValues={field.name === "languages" ? true : false}
            useDefaultValue={true}
            limitOptionsToDefaultValue={true}
            searchEndpoint={
              config && config?.searchEndpoint
                ? config?.searchEndpoint
                : undefined
            }
            overrideOptions={overrideOptions}
            readOnly={readOnly}
            addZIndexToMenu={addZIndexToMenu}
            callSearchEndpointOnClick={callSearchEndpointOnClick}
          />
        </div>
      );
    } else if (type === "multiselect") {
      return (
        <div
          key={`multiselect-${_key}`}
          className="FormBox FormSideBySide"
          style={maxHeight ? { maxHeight: `${maxHeight}` } : undefined}
        >
          <div className="FormLabel">
            <label>{`${field.label}`}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <SingleSelectSearchList
            name={String(field.name)}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            showUseDefaultValues={field.name === "languages" ? true : false}
            useDefaultValue={true}
            limitOptionsToDefaultValue={true}
            isMulti={true}
            searchEndpoint={
              config && config?.searchEndpoint
                ? config?.searchEndpoint
                : undefined
            }
            readOnly={readOnly}
            addZIndexToMenu={addZIndexToMenu}
            callSearchEndpointOnClick={callSearchEndpointOnClick}
          />
        </div>
      );
    } else if (type === "multiselectOrderNumbers") {
      return (
        <div
          key={`multiselect-${_key}`}
          className="FormBox FormSideBySide"
          style={maxHeight ? { maxHeight: `${maxHeight}` } : undefined}
        >
          <div className="FormLabel">
            <label>{`${field.label}`}</label>

            {showEditIcon && (
              <img src={editIcon} alt="edit icon" className="EditIcon" />
            )}
          </div>
          <SingleSelectSearchListOrderNumbers
            name={String(field.name)}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            showUseDefaultValues={field.name === "languages" ? true : false}
            useDefaultValue={true}
            limitOptionsToDefaultValue={true}
            isMulti={true}
            searchEndpoint={
              config && config?.searchEndpoint
                ? config?.searchEndpoint
                : undefined
            }
            textToDisplayAtTop={textToDisplayAtTop}
            callSearchEndpointOnClick={callSearchEndpointOnClick}
          />
        </div>
      );
    } else if (type === "multiselectWithLinks") {
      return (
        <div
          key={`multiselectWithLinks-${_key}`}
          className="FormBox FormSideBySide"
        >
          <MultiSelectSearchListWithLinks
            name={String(field.name)}
            singleSelect={false}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            label={String(field.label)}
            afterLabelText={
              afterLabel && afterLabel.hasOwnProperty(field.name)
                ? afterLabel[field.name as keyof typeof afterLabel]
                : undefined
            }
            readOnly={readOnly}
            editDisable={editDisabled}
            expandable={expandableFieldNames?.includes(field.name as string)}
            {...config}
            nestedFormModule={nestedFormModule}
            overrideOptions={overrideOptions}
            showEditIcon={showEditIcon}
            maxHeight={maxHeight}
            addZIndexToMenu={addZIndexToMenu}
            callSearchEndpointOnClick={callSearchEndpointOnClick}
          />
        </div>
      );
    } else if (type === "rolesMultiSelectWithLinks") {
      return (
        <div
          key={`multiselectWithLinks-${_key}`}
          className="FormBox FormSideBySide"
        >
          <RolesMultiSelectSearchListWithLinks
            name={String(field.name)}
            singleSelect={false}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            {...config}
            label={String(field.label)}
            callSearchEndpointOnClick={callSearchEndpointOnClick}
          />
        </div>
      );
    } else {
      return (
        <div
          key={`singleselectWithLinks-${_key}`}
          className="FormBox FormSideBySide"
        >
          <SingleSelectSearchListWithLinks
            name={String(field.name)}
            singleSelect={true}
            dataSource={
              field.dataSource && typeof field.dataSource === "string"
                ? pascalCaseToCamelCase(field.dataSource)
                : null
            }
            label={String(field.label)}
            afterLabelText={
              afterLabel && afterLabel.hasOwnProperty(field.name)
                ? afterLabel[field.name as keyof typeof afterLabel]
                : undefined
            }
            readOnly={readOnly}
            nestedFormModule={nestedFormModule}
            {...config}
            overrideOptions={overrideOptions}
            showEditIcon={showEditIcon}
            maxHeight={maxHeight}
            addZIndexToMenu={addZIndexToMenu}
            callSearchEndpointOnClick={callSearchEndpointOnClick}
          />
        </div>
      );
    }
  };

export const searchListReadOnlyFieldRenderer = (
  type: "singleselect" | "multiselect",
  extraButton?: () => React.ReactNode
) =>
  function searchListFieldRenderer<T>(i: React.ReactText, field: FormField<T>) {
    const hasBtn = !!extraButton;
    switch (type) {
      case "singleselect":
        return (
          <div key={i} className="FormBox FormSideBySide">
            <div className={`FormLabel ${hasBtn ? "NextTo" : ""}`}>
              <label>{field.label}</label>

              {field.toolTip && (
                <FormFieldTooltip
                  tooltipText={field.toolTip}
                  tooltipMainIconSource={tooltipMainIconSource}
                  tooltipCloseIconSource={tooltipCloseIconSource}
                />
              )}
              {hasBtn && extraButton()}
            </div>
            <div className="FormField">
              <EditSingleSelectReadOnly
                name={String(field.name)}
                dataSource={
                  field.dataSource && typeof field.dataSource === "string"
                    ? pascalCaseToCamelCase(field.dataSource)
                    : null
                }
              />
            </div>
          </div>
        );
      case "multiselect":
        return (
          <div key={i} className="FormBox FormSideBySide">
            <div className={`FormLabel ${hasBtn ? "NextTo" : ""}`}>
              <label>{field.label}</label>
              {field.toolTip && (
                <FormFieldTooltip
                  tooltipText={field.toolTip}
                  tooltipMainIconSource={tooltipMainIconSource}
                  tooltipCloseIconSource={tooltipCloseIconSource}
                />
              )}
              {hasBtn && extraButton()}
            </div>
            <div className="FormField">
              <EditMultiSelectReadOnly
                name={String(field.name)}
                dataSource={
                  field.dataSource && typeof field.dataSource === "string"
                    ? pascalCaseToCamelCase(field.dataSource)
                    : null
                }
                showUseRoleValues={
                  field.name === "adminUserRoles" ? true : false
                }
              />
            </div>
          </div>
        );
    }
  };

export function formatPropertyName(propertyName: string) {
  // Split camelCase string into words
  const words = propertyName.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");

  // Capitalize the first letter of each word
  const formattedWords = words.map(
    word => word.charAt(0).toUpperCase() + word.slice(1)
  );

  // Join the words into a formatted string
  return formattedWords.join(" ");
}
